import { Card, Grid, Paper, Typography } from '@material-ui/core';
import React from 'react';

import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { useDashboardCardStyles } from '~/src/dash/views/ias-publisher/IASDashboard/common/consts';

const getCompareTextClass = (
  isPercentChangeNegative: boolean,
  isInverseMetric: boolean,
  classes: Record<string, string>,
) => {
  if (isInverseMetric) {
    return isPercentChangeNegative
      ? classes.dashboardCardComparePositive
      : classes.dashboardCardCompareNegative;
  } else {
    return isPercentChangeNegative
      ? classes.dashboardCardCompareNegative
      : classes.dashboardCardComparePositive;
  }
};

const getValueTextClass = (
  loadStatus: number,
  isActive: boolean,
  classes: Record<string, string>,
) => {
  if (loadStatus !== 1) {
    if (isActive) {
      return classes.dashboardCardValueSmallWhite;
    } else {
      return classes.dashboardCardValueSmallDark;
    }
  } else {
    if (isActive) {
      return classes.dashboardCardValueWhite;
    } else {
      return classes.dashboardCardValueDark;
    }
  }
};

const ComparisonArrow = ({ isPercentChangeNegative, isInverseMetric }: Record<string, boolean>) => {
  switch (true) {
    case isPercentChangeNegative && !isInverseMetric:
      return <img src='/assets/shared/svg/RedArrow.svg' style={{ transform: 'rotate(180deg)' }} />;
    case isPercentChangeNegative && isInverseMetric:
      return <img src='/assets/shared/svg/Arrow.svg' style={{ transform: 'rotate(180deg)' }} />;
    case !isPercentChangeNegative && !isInverseMetric:
      return <img src='/assets/shared/svg/Arrow.svg' />;
    default:
      return <img src='/assets/shared/svg/RedArrow.svg' />;
  }
};

const displayValue = (value: string, loadStatus: number) => {
  switch (loadStatus) {
    case LoadingStateLoading:
      return 'Loading...';
    case LoadingStateHasData:
      return value;
    case LoadingStateNoData:
      return 'No Data';
    default:
      return 'N/A';
  }
};

interface IIASDashboardCardProps {
  color: string;
  isComparedToEnabled?: boolean;
  isInverseMetric?: boolean;
  isPercentChangeNegative?: boolean;
  label: string;
  percentChange?: string;
  value: string;
  isActive: boolean;
  handleClickCard: (key: string) => void;
  loadStatus: number;
  dataKey: string;
}

export const IASDashboardCard = ({
  color,
  isComparedToEnabled = false,
  isInverseMetric = false,
  isPercentChangeNegative = false,
  label,
  percentChange,
  value,
  isActive,
  handleClickCard,
  loadStatus,
  dataKey,
}: IIASDashboardCardProps) => {
  const classes = useDashboardCardStyles();

  return (
    <Grid item>
      <Paper
        className={classes.paperCards}
        style={{
          backgroundColor: isActive ? color : 'white',
          cursor: 'pointer',
        }}
        onClick={() => handleClickCard(dataKey)}
      >
        {/* CARD LABEL */}
        <Grid
          style={{
            background: isActive ? color : 'white',
            height: 32,
          }}
        >
          <Card
            className={classes.dashboardTitleCard}
            style={{ backgroundColor: isActive ? color : 'white' }}
          >
            <Grid
              style={{
                background: isActive ? color : 'white',
                paddingTop: 4,
                paddingLeft: 8,
                paddingRight: 8,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              <Typography
                className={
                  isActive ? classes.dashboardCardTitleWhite : classes.dashboardCardTitleDark
                }
              >
                {label}
              </Typography>
            </Grid>
          </Card>
        </Grid>
        {/* CARD VALUE */}
        <Grid
          container
          direction='column'
          alignItems='flex-start'
          style={{
            justifyContent: 'flex-end',
            height: '100%',
            width: '100%',
            backgroundColor: isActive ? color : 'white',
          }}
        >
          <Typography className={getValueTextClass(loadStatus, isActive, classes)}>
            {displayValue(value, loadStatus)}
          </Typography>
        </Grid>
        {/* CARD CHANGE VALUE */}
        {isComparedToEnabled && loadStatus === LoadingStateHasData && (
          <Grid
            container
            direction='row'
            alignItems='center'
            style={{
              height: '100%',
              width: '100%',
              paddingLeft: 12,
              backgroundColor: '#FFFFFF',
              borderTop: '0.5px solid lightgrey',
              borderBottomLeftRadius: 2,
              borderBottomRightRadius: 2,
            }}
          >
            <Grid item style={{ padding: '0px 0px 2px 0px' }}>
              <ComparisonArrow
                isPercentChangeNegative={isPercentChangeNegative}
                isInverseMetric={isInverseMetric}
              />
            </Grid>
            <Grid item>
              <Typography
                className={getCompareTextClass(isPercentChangeNegative, isInverseMetric, classes)}
              >
                {percentChange}
              </Typography>
            </Grid>
            <Grid item>
              <Typography className={classes.dashboardCardText}>/ previous period</Typography>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Grid>
  );
};
